/**
 * CapsuleShadowsNode — analytic character shadows from capsule proxies (the
 * UE Capsule Shadows / TLOU analytic approach): each character is a handful
 * of capsules; a post pass reconstructs the world position per pixel, casts
 * a ray toward the light, and darkens by the capsules' angular occlusion of
 * that ray — soft, cheap, works in baked/indirect lighting where shadow maps
 * have nothing to offer.
 *
 * Occlusion model (per capsule): treat the closest point of the capsule
 * segment to the shadow ray as a sphere of the capsule radius; the sphere's
 * angular radius α = asin(r/d) versus the ray's angular miss θ gives
 *   occ = 1 − linstep(α, α + penumbra, θ)
 * scaled by the light-facing test. The maximum over capsules wins (limbs
 * overlap; summing would over-darken).
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, min, max, clamp, saturate,
  normalize, acos, asin, dot, perspectiveDepthToViewZ, cameraPosition, cameraWorldMatrix,
} from 'three/tsl';

export const MAX_SHADOW_CAPSULES = 8;

export interface CapsuleDef {
  a: [number, number, number];
  b: [number, number, number];
  radius: number;
}

export interface CapsuleShadowsPassConfig {
  /** World-space light direction the shadows cast AWAY from (default sun-ish). */
  lightDirection?: [number, number, number];
  /** Maximum darkening 0..1 (default 0.65). */
  intensity?: number;
  /** Penumbra angle in radians added past the hard cone (default 0.25). */
  penumbra?: number;
  /** Occlusion fades out past this distance from the capsule (default 6). */
  maxDistance?: number;
  enabled?: boolean;
}

export interface CapsuleShadowsUniforms {
  lightDirection: { value: THREE.Vector3 };
  intensity: { value: number };
  penumbra: { value: number };
  maxDistance: { value: number };
  near: { value: number };
  far: { value: number };
  tanHalfFovY: { value: number };
  aspect: { value: number };
  capsuleA: Array<{ value: THREE.Vector3 }>;
  capsuleB: Array<{ value: THREE.Vector3 }>;
  capsuleRadius: Array<{ value: number }>;
  setCapsule(index: number, capsule: CapsuleDef | null): void;
}

// ─── CPU mirror (exported for tests) ────────────────────────────────────────

/**
 * Occlusion of a shadow ray (origin p, direction l, unit) by a sphere at c
 * with radius r: angular-overlap soft term.
 */
export function sphereRayOcclusionCpu(
  p: [number, number, number], l: [number, number, number],
  c: [number, number, number], r: number,
  penumbra: number, maxDistance: number,
): number {
  const to = [c[0] - p[0], c[1] - p[1], c[2] - p[2]];
  const d = Math.hypot(to[0], to[1], to[2]);
  if (d < 1e-6) return 1; // inside the occluder
  if (d > maxDistance) return 0;
  const cosT = (to[0] * l[0] + to[1] * l[1] + to[2] * l[2]) / d;
  if (cosT <= 0) return 0; // occluder behind the surface w.r.t. the light
  const theta = Math.acos(Math.min(Math.max(cosT, -1), 1));
  const alpha = Math.asin(Math.min(r / d, 1));
  const t = (theta - alpha) / Math.max(penumbra, 1e-4);
  const s = t < 0 ? 0 : (t > 1 ? 1 : t);
  const distFade = 1 - Math.min(d / maxDistance, 1);
  return (1 - s * s * (3 - 2 * s)) * distFade;
}

/** Closest point on segment ab to point p (CPU, exported for tests). */
export function closestPointOnSegmentCpu(
  p: [number, number, number], a: [number, number, number], b: [number, number, number],
): [number, number, number] {
  const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const ap = [p[0] - a[0], p[1] - a[1], p[2] - a[2]];
  const len2 = ab[0] * ab[0] + ab[1] * ab[1] + ab[2] * ab[2];
  const t = len2 < 1e-9 ? 0 : Math.min(Math.max(
    (ap[0] * ab[0] + ap[1] * ab[1] + ap[2] * ab[2]) / len2, 0), 1);
  return [a[0] + ab[0] * t, a[1] + ab[1] * t, a[2] + ab[2] * t];
}

// ─── Pass factory ───────────────────────────────────────────────────────────

export function createCapsuleShadowsNode(
  input: unknown,
  depthNode: unknown,
  camera: THREE.Camera,
  config?: CapsuleShadowsPassConfig,
): { node: unknown; uniforms: CapsuleShadowsUniforms } {
  const cam = camera as THREE.PerspectiveCamera;
  const ld = config?.lightDirection ?? [0.3, 0.8, 0.5];
  const uLightDir = uniform(vec3(ld[0], ld[1], ld[2]));
  const uIntensity = uniform(config?.intensity ?? 0.65);
  const uPenumbra = uniform(config?.penumbra ?? 0.25);
  const uMaxDist = uniform(config?.maxDistance ?? 6);
  const uNear = uniform(cam.near ?? 0.1);
  const uFar = uniform(cam.far ?? 1000);
  const uTanHalf = uniform(Math.tan(((cam.fov ?? 60) * Math.PI) / 360));
  const uAspect = uniform(cam.aspect ?? 1);

  const capsuleA: Array<ReturnType<typeof uniform>> = [];
  const capsuleB: Array<ReturnType<typeof uniform>> = [];
  const capsuleRadius: Array<ReturnType<typeof uniform>> = [];
  for (let i = 0; i < MAX_SHADOW_CAPSULES; i++) {
    capsuleA.push(uniform(vec3(0, 0, 0)));
    capsuleB.push(uniform(vec3(0, 0, 0)));
    capsuleRadius.push(uniform(0));
  }

  const build = Fn(() => {
    const cIn = vec4(input as never);
    const depthScalar = ((depthNode as { r?: unknown }).r ?? depthNode) as never;
    const viewZ = perspectiveDepthToViewZ(depthScalar, uNear, uFar);
    const ndc = uv().mul(2).sub(vec2(1, 1));
    const rayView = vec3(ndc.x.mul(uTanHalf).mul(uAspect), ndc.y.mul(uTanHalf), float(-1));
    const worldDir = normalize(cameraWorldMatrix.mul(vec4(rayView, 0)).xyz);
    const dist = viewZ.negate().mul(rayView.length());
    const worldPos = cameraPosition.add(worldDir.mul(dist));
    const l = normalize(uLightDir);

    let occ: ReturnType<typeof float> = float(0);
    for (let i = 0; i < MAX_SHADOW_CAPSULES; i++) {
      const a = capsuleA[i] as never as ReturnType<typeof vec3>;
      const b = capsuleB[i] as never as ReturnType<typeof vec3>;
      const r = capsuleRadius[i] as never as ReturnType<typeof float>;
      // Closest point on the segment to the surface point.
      const ab = b.sub(a);
      const t = clamp(
        dot(worldPos.sub(a), ab).div(dot(ab, ab).add(1e-9)),
        float(0), float(1),
      );
      const c = a.add(ab.mul(t));
      const to = c.sub(worldPos);
      const d = to.length().add(1e-6);
      const cosT = saturate(dot(to.div(d), l));
      const theta = acos(clamp(cosT, float(-1), float(1)));
      const alpha = asin(clamp(r.div(d), float(0), float(1)));
      const s = clamp(theta.sub(alpha).div(uPenumbra), float(0), float(1));
      const smooth = s.mul(s).mul(float(3).sub(s.mul(2)));
      const distFade = saturate(float(1).sub(d.div(uMaxDist)));
      // Zero-radius slots contribute nothing (alpha 0 → s ≥ 0 fast).
      const active = r.greaterThan(0).select(float(1), float(0));
      occ = max(occ, float(1).sub(smooth).mul(distFade).mul(active)) as ReturnType<typeof float>;
    }

    const shade = float(1).sub(min(occ.mul(uIntensity), uIntensity));
    return vec4(cIn.rgb.mul(shade), cIn.a);
  });

  const asVec3 = (u: ReturnType<typeof uniform>): { value: THREE.Vector3 } =>
    u as unknown as { value: THREE.Vector3 };
  const asNum = (u: ReturnType<typeof uniform>): { value: number } =>
    u as unknown as { value: number };

  const uniforms: CapsuleShadowsUniforms = {
    lightDirection: asVec3(uLightDir),
    intensity: asNum(uIntensity),
    penumbra: asNum(uPenumbra),
    maxDistance: asNum(uMaxDist),
    near: asNum(uNear),
    far: asNum(uFar),
    tanHalfFovY: asNum(uTanHalf),
    aspect: asNum(uAspect),
    capsuleA: capsuleA.map(asVec3),
    capsuleB: capsuleB.map(asVec3),
    capsuleRadius: capsuleRadius.map(asNum),
    setCapsule(index: number, capsule: CapsuleDef | null): void {
      if (index < 0 || index >= MAX_SHADOW_CAPSULES) return;
      if (!capsule) {
        this.capsuleRadius[index].value = 0;
        return;
      }
      this.capsuleA[index].value.set(...capsule.a);
      this.capsuleB[index].value.set(...capsule.b);
      this.capsuleRadius[index].value = capsule.radius;
    },
  };

  return { node: build(), uniforms };
}
